import { Lock, School } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useLocation, type RouteObject } from 'react-router';
import { AppLayout } from './components/Layout';
import { AddOnRequired, PlanRequired } from './components/plan';
import { Button, EmptyState, Spinner } from './components/ui';
import { useAuth } from './lib/auth';
import { usePlan } from './lib/billing';
import { DeskProvider } from './lib/desk';
import { currentSchool, openSchool, schoolHref } from './lib/school';
import type { TenantRole } from './lib/types';
import { ResetPasswordPage, VerifyEmailPage } from './pages/AccountEmailPages';
import { AssetDetailPage } from './pages/AssetDetailPage';
import { AssetsPage } from './pages/AssetsPage';
import { AutomationPage } from './pages/AutomationPage';
import { BillingPage } from './pages/BillingPage';
import { BlackbaudLinkPage } from './pages/BlackbaudLinkPage';
import { CartDetailPage } from './pages/CartDetailPage';
import { CartsPage } from './pages/CartsPage';
import { CheckoutBulkPage } from './pages/CheckoutBulkPage';
import { DeparturesPage } from './pages/DeparturesPage';
import { CheckoutsPage } from './pages/CheckoutsPage';
import { CheckoutStationPage } from './pages/CheckoutStationPage';
import { VerifyAssetsPage } from './pages/VerifyAssetsPage';
import { DashboardPage } from './pages/DashboardPage';
import { EventRequestDetailPage } from './pages/events/EventRequestDetailPage';
import { EventRequestFormPage } from './pages/events/EventRequestFormPage';
import { EventRequestsPage } from './pages/events/EventRequestsPage';
import { DocumentsPage } from './pages/facilities/DocumentsPage';
import { EquipmentDetailPage } from './pages/facilities/EquipmentDetailPage';
import { EquipmentPage } from './pages/facilities/EquipmentPage';
import { FacilitiesDashboardPage } from './pages/facilities/FacilitiesDashboardPage';
import { InspectionDetailPage } from './pages/facilities/InspectionDetailPage';
import { InspectionsPage } from './pages/facilities/InspectionsPage';
import { KnowledgeEditorPage } from './pages/knowledge/KnowledgeEditorPage';
import { KnowledgeArticlePage, KnowledgeListPage } from './pages/knowledge/KnowledgePages';
import { PublicKnowledgeArticlePage, PublicKnowledgePage } from './pages/knowledge/PublicKnowledgePages';
import { MaintenancePage } from './pages/facilities/MaintenancePage';
import { GoogleDeviceDetailPage } from './pages/GoogleDeviceDetailPage';
import { GoogleDevicesPage } from './pages/GoogleDevicesPage';
import { HelpdeskSetupPage } from './pages/HelpdeskSetupPage';
import { IncidentDetailPage } from './pages/incidents/IncidentDetailPage';
import { IncidentsPage } from './pages/incidents/IncidentsPage';
import { InvoiceDetailPage } from './pages/incidents/InvoiceDetailPage';
import { IntegrationDetailPage, IntegrationsPage } from './pages/IntegrationsPage';
import { LoanersPage } from './pages/LoanersPage';
import { LocationsPage } from './pages/LocationsPage';
import { LoginPage } from './pages/LoginPage';
import { ManagedDeviceDetailPage } from './pages/ManagedDeviceDetailPage';
import { ManagedDevicesPage } from './pages/ManagedDevicesPage';
import { MyRequestsPage } from './pages/MyRequestsPage';
import { PartDetailPage } from './pages/PartDetailPage';
import { PartsPage } from './pages/PartsPage';
import { PeoplePage } from './pages/PeoplePage';
import { PersonDetailPage } from './pages/PersonDetailPage';
import { IntegrationRequestsPage } from './pages/IntegrationRequestsPage';
import { PlatformAdminsPage } from './pages/PlatformAdminsPage';
import { PlatformStripePage } from './pages/PlatformStripePage';
import { PlatformPage } from './pages/PlatformPage';
import { RequesterHomePage } from './pages/RequesterHomePage';
import { ReportsPage } from './pages/ReportsPage';
import { RoomDetailPage } from './pages/RoomDetailPage';
import { RoomRolloverPage } from './pages/RoomRolloverPage';
import { SchoolLoginPage } from './pages/SchoolLoginPage';
import { SettingsPage } from './pages/SettingsPage';
import { FleetPage } from './pages/device-location/FleetPage';
import { LocationSettingsPage } from './pages/device-location/LocationSettingsPage';
import { SignUpPage } from './pages/SignUpPage';
import { WorkflowEditorPage } from './pages/WorkflowEditorPage';
import { WorkOrderDetailPage } from './pages/WorkOrderDetailPage';
import { WorkOrdersPage } from './pages/WorkOrdersPage';

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner className="size-7" />
    </div>
  );
}

function RequireAuth() {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageSpinner />;
  if (!me) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  return <AppLayout />;
}

/** Signed in, without the app's navigation: for pages opened in a pop-up, like linking a Blackbaud account. */
function RequireSignIn({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageSpinner />;
  if (!me) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  return children;
}

/** Signed in, but this account can't open the school in the URL. */
function NoSchoolAccess() {
  const { me, logout } = useAuth();
  const elsewhere = me?.memberships.find((m) => m.slug !== currentSchool);
  const googleOnly = me?.authMethod === 'PASSWORD';
  return (
    <div className="space-y-4">
      <EmptyState
        icon={<Lock />}
        title="You don’t have access to this school"
        description={
          googleOnly
            ? `This school may require its Google sign-in. Sign out, then choose “Sign in with Google” with your school account.`
            : `${me?.user.email} isn’t a member of this school. Ask its technology team for access.`
        }
      />
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          variant="primary"
          onClick={async () => {
            await logout();
            window.location.assign(schoolHref(currentSchool!, '/login'));
          }}
        >
          Sign in with a different account
        </Button>
        {elsewhere && <Button onClick={() => openSchool(elsewhere.slug)}>Open {elsewhere.name}</Button>}
      </div>
    </div>
  );
}

/**
 * Pages that operate inside a school. Staff pages require at least Viewer.
 * `unpaid` pages open while the school's Edvora plan has lapsed (Plan & billing).
 */
function School_({ children, minRole = 'VIEWER', unpaid = false }: { children: ReactNode; minRole?: TenantRole; unpaid?: boolean }) {
  const { me, can, canFacilities } = useAuth();
  const plan = usePlan();
  if (!me?.tenant) return <NoSchoolAccess />;
  if (!can(minRole)) {
    // Requesters only have the help desks; send them to the one they work in instead of a dead end.
    if (me.role === 'REQUESTER') return <Navigate to={canFacilities('VIEWER') ? '/facilities' : '/'} replace />;
    return <EmptyState icon={<Lock />} title="You don’t have access to this page" description="Ask a school administrator if you need it." />;
  }
  if (plan.locked && !unpaid) return <PlanRequired />;
  return children;
}

/** The school's front page: the dashboard for staff, and the requester portal's home for everyone else. */
function SchoolHome() {
  const { me, can, canFacilities } = useAuth();
  const plan = usePlan();
  if (!me?.tenant) return <NoSchoolAccess />;
  if (plan.locked) return <PlanRequired />;
  if (can('VIEWER')) return <DashboardPage />;
  // The facilities team starts on its own overview.
  if (canFacilities('VIEWER')) return <Navigate to="/facilities" replace />;
  return <RequesterHomePage />;
}

/** Facilities pages: the school has Facilities, and the user has at least `minRole` on the facilities team. */
function Facilities_({ children, minRole = 'VIEWER' }: { children: ReactNode; minRole?: TenantRole }) {
  const { me, canFacilities } = useAuth();
  const plan = usePlan();
  if (!me?.tenant) return <NoSchoolAccess />;
  if (plan.locked) return <PlanRequired />;
  if (!me.tenant.facilitiesEnabled) {
    if (!plan.has('FACILITIES')) {
      return (
        <AddOnRequired
          feature="FACILITIES"
          description="Facilities adds a help desk for maintenance requests, preventive maintenance, inspections, equipment, and documents."
        />
      );
    }
    return <EmptyState icon={<Lock />} title="Facilities isn’t turned on for this school" description="Edvora turns on Facilities for schools that use it." />;
  }
  if (!canFacilities(minRole)) {
    // Everyone at a school with Facilities can send requests, so send them there.
    if (canFacilities('REQUESTER')) return <Navigate to="/facilities/requests" replace />;
    return <EmptyState icon={<Lock />} title="You don’t have access to this page" description="Ask a school administrator for a facilities role if you need it." />;
  }
  return <DeskProvider service="FACILITIES">{children}</DeskProvider>;
}

function PlatformOnly({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  if (!me?.user.isPlatformAdmin) return <Navigate to="/" replace />;
  return children;
}

function NotFound() {
  return <EmptyState title="Page not found" description="The page you were looking for doesn’t exist." />;
}

/** The Edvora front door (/): sends people to the school they last used, or to sign in. */
function RootHome() {
  const { me, loading } = useAuth();
  const target = me?.tenant?.slug ?? me?.memberships[0]?.slug ?? null;
  useEffect(() => {
    if (target) openSchool(target);
  }, [target]);
  if (loading || target) return <FullPageSpinner />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.user.isPlatformAdmin) return <Navigate to="/platform" replace />;
  return <EmptyState icon={<School />} title="No school selected" description="Your account is not linked to a school yet." />;
}

/** /{school}/... - the router's basename is the school, so every in-app link stays inside it. */
const schoolRoutes: RouteObject[] = [
  { path: '/login', element: <SchoolLoginPage /> },
  { path: '/blackbaud-link', element: <RequireSignIn><BlackbaudLinkPage /></RequireSignIn> },
  // The public knowledgebase, linked from the sign-in page: no sign-in needed.
  { path: '/help', element: <PublicKnowledgePage /> },
  { path: '/help/:id', element: <PublicKnowledgeArticlePage /> },
  {
    element: <RequireAuth />,
    children: [
      { index: true, element: <SchoolHome /> },
      { path: 'assets', element: <School_><AssetsPage /></School_> },
      { path: 'assets/:id', element: <School_><AssetDetailPage /></School_> },
      { path: 'carts', element: <School_><CartsPage /></School_> },
      { path: 'carts/:id', element: <School_><CartDetailPage /></School_> },
      { path: 'parts', element: <School_><PartsPage /></School_> },
      { path: 'parts/:id', element: <School_><PartDetailPage /></School_> },
      { path: 'station', element: <School_ minRole="TECHNICIAN"><CheckoutStationPage /></School_> },
      { path: 'verify', element: <School_ minRole="TECHNICIAN"><VerifyAssetsPage /></School_> },
      { path: 'checkouts', element: <School_><CheckoutsPage /></School_> },
      { path: 'checkouts/bulk', element: <School_ minRole="TECHNICIAN"><CheckoutBulkPage /></School_> },
      { path: 'departures', element: <School_ minRole="VIEWER"><DeparturesPage /></School_> },
      { path: 'loaners', element: <School_><LoanersPage /></School_> },
      // Incidents and damage invoices: technicians and administrators.
      { path: 'incidents', element: <School_ minRole="TECHNICIAN"><IncidentsPage /></School_> },
      { path: 'incidents/:id', element: <School_ minRole="TECHNICIAN"><IncidentDetailPage /></School_> },
      { path: 'invoices/:id', element: <School_ minRole="TECHNICIAN"><InvoiceDetailPage /></School_> },
      { path: 'work-orders', element: <School_><WorkOrdersPage /></School_> },
      { path: 'work-orders/:id', element: <School_ minRole="REQUESTER"><WorkOrderDetailPage /></School_> },
      { path: 'automation', element: <School_ minRole="TECHNICIAN"><AutomationPage /></School_> },
      { path: 'automation/workflows/:id', element: <School_ minRole="TECHNICIAN"><WorkflowEditorPage /></School_> },
      { path: 'helpdesk-setup', element: <School_ minRole="TECHNICIAN"><HelpdeskSetupPage /></School_> },
      { path: 'requests', element: <School_ minRole="REQUESTER"><MyRequestsPage /></School_> },
      // Event setup requests open work orders on both help desks, so they live outside either one.
      { path: 'events', element: <School_ minRole="REQUESTER"><EventRequestsPage /></School_> },
      { path: 'events/new', element: <School_ minRole="REQUESTER"><EventRequestFormPage /></School_> },
      { path: 'events/:id', element: <School_ minRole="REQUESTER"><EventRequestDetailPage /></School_> },
      // The knowledgebase: everyone reads what's meant for them; technicians and administrators write.
      { path: 'knowledge', element: <School_ minRole="REQUESTER"><KnowledgeListPage /></School_> },
      { path: 'knowledge/new', element: <School_ minRole="TECHNICIAN"><KnowledgeEditorPage /></School_> },
      { path: 'knowledge/:id', element: <School_ minRole="REQUESTER"><KnowledgeArticlePage /></School_> },
      { path: 'knowledge/:id/edit', element: <School_ minRole="TECHNICIAN"><KnowledgeEditorPage /></School_> },
      { path: 'reports', element: <School_><ReportsPage /></School_> },
      // Facilities: its own help desk, with its own roles.
      { path: 'facilities', element: <Facilities_><FacilitiesDashboardPage /></Facilities_> },
      { path: 'facilities/work-orders', element: <Facilities_><WorkOrdersPage /></Facilities_> },
      { path: 'facilities/work-orders/:id', element: <Facilities_ minRole="REQUESTER"><WorkOrderDetailPage /></Facilities_> },
      { path: 'facilities/requests', element: <Facilities_ minRole="REQUESTER"><MyRequestsPage /></Facilities_> },
      { path: 'facilities/maintenance', element: <Facilities_><MaintenancePage /></Facilities_> },
      { path: 'facilities/inspections', element: <Facilities_><InspectionsPage /></Facilities_> },
      { path: 'facilities/inspections/:id', element: <Facilities_><InspectionDetailPage /></Facilities_> },
      { path: 'facilities/equipment', element: <Facilities_><EquipmentPage /></Facilities_> },
      { path: 'facilities/equipment/:id', element: <Facilities_><EquipmentDetailPage /></Facilities_> },
      { path: 'facilities/documents', element: <Facilities_><DocumentsPage /></Facilities_> },
      { path: 'facilities/reports', element: <Facilities_><ReportsPage /></Facilities_> },
      { path: 'facilities/setup', element: <Facilities_ minRole="TECHNICIAN"><HelpdeskSetupPage /></Facilities_> },
      { path: 'people', element: <School_><PeoplePage /></School_> },
      { path: 'people/:id', element: <School_><PersonDetailPage /></School_> },
      { path: 'locations', element: <School_><LocationsPage /></School_> },
      { path: 'locations/rooms/:id', element: <School_><RoomDetailPage /></School_> },
      { path: 'locations/rollover', element: <School_ minRole="TECHNICIAN"><RoomRolloverPage /></School_> },
      { path: 'devices', element: <School_><GoogleDevicesPage /></School_> },
      { path: 'devices/:id', element: <School_><GoogleDeviceDetailPage /></School_> },
      { path: 'managed-devices', element: <School_><ManagedDevicesPage /></School_> },
      { path: 'managed-devices/:id', element: <School_><ManagedDeviceDetailPage /></School_> },
      { path: 'device-location', element: <School_><FleetPage /></School_> },
      { path: 'device-location/settings', element: <School_ minRole="ADMIN"><LocationSettingsPage /></School_> },
      { path: 'integrations', element: <School_ minRole="TECHNICIAN"><IntegrationsPage /></School_> },
      { path: 'integrations/:provider', element: <School_ minRole="TECHNICIAN"><IntegrationDetailPage /></School_> },
      { path: 'settings', element: <School_ minRole="REQUESTER"><SettingsPage /></School_> },
      { path: 'billing', element: <School_ minRole="ADMIN" unpaid><BillingPage /></School_> },
      { path: '*', element: <NotFound /> },
    ],
  },
];

/** Edvora-wide pages: /login and the platform console. */
const rootRoutes: RouteObject[] = [
  { path: '/', element: <RootHome /> },
  { path: '/login', element: <LoginPage /> },
  // Schools create their own account: a free trial, or a subscription.
  { path: '/sign-up', element: <SignUpPage /> },
  // Links in Edvora's account emails.
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  {
    element: <RequireAuth />,
    children: [
      { path: 'platform', element: <PlatformOnly><PlatformPage /></PlatformOnly> },
      { path: 'platform/admins', element: <PlatformOnly><PlatformAdminsPage /></PlatformOnly> },
      { path: 'platform/stripe', element: <PlatformOnly><PlatformStripePage /></PlatformOnly> },
      { path: 'platform/integration-requests', element: <PlatformOnly><IntegrationRequestsPage /></PlatformOnly> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];

export const router = currentSchool
  ? createBrowserRouter(schoolRoutes, { basename: `/${currentSchool}` })
  : createBrowserRouter(rootRoutes);
